import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PrimeNG } from 'primeng/config';
import { Translation } from 'primeng/api';

const PRIMENG_LOCALE_PL: Translation = {
  firstDayOfWeek: 1,
  dayNames: ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'],
  dayNamesShort: ['Nie', 'Pon', 'Wto', 'Śro', 'Czw', 'Pią', 'Sob'],
  dayNamesMin: ['Nd', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So'],
  monthNames: ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'],
  monthNamesShort: ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'],
  today: 'Dziś',
  clear: 'Wyczyść',
  weekHeader: 'Tydz',
  accept: 'Tak',
  reject: 'Nie',
  emptyMessage: 'Brak wyników',
  emptyFilterMessage: 'Brak wyników',
};

const PRIMENG_LOCALE_EN: Translation = {
  firstDayOfWeek: 1,
  dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  today: 'Today',
  clear: 'Clear',
  weekHeader: 'Wk',
  accept: 'Yes',
  reject: 'No',
  emptyMessage: 'No results found',
  emptyFilterMessage: 'No results found',
};

const PRIMENG_LOCALES: Record<string, Translation> = {
  pl: PRIMENG_LOCALE_PL,
  en: PRIMENG_LOCALE_EN,
};

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly LANGUAGE_KEY = 'userLanguage';
  private readonly AVAILABLE_LANGS = ['en', 'pl'];
  private readonly transloco = inject(TranslocoService);
  private readonly primeng = inject(PrimeNG);
  public language: WritableSignal<string>;

  constructor() {
    const storedLang = this.getStoredLanguage();
    const browserLang = this.getBrowserLanguage();
    const lang = storedLang || browserLang || 'pl';
    const resolvedLang = this.isSupported(lang) ? lang : 'pl';
    this.language = signal(resolvedLang);
    this.transloco.setActiveLang(resolvedLang);
    this.updatePrimeNGLocale(resolvedLang);
  }

  private getStoredLanguage(): string | null {
    return localStorage.getItem(this.LANGUAGE_KEY);
  }

  private storeLanguage(lang: string): void {
    localStorage.setItem(this.LANGUAGE_KEY, lang);
  }

  private getBrowserLanguage(): string {
    return navigator.language.split('-')[0];
  }

  private isSupported(lang: string): boolean {
    return this.AVAILABLE_LANGS.includes(lang);
  }

  private updatePrimeNGLocale(lang: string): void {
    const locale = PRIMENG_LOCALES[lang] ?? PRIMENG_LOCALE_PL;
    this.primeng.setTranslation(locale);
  }

  public setLanguage(lang: string): void {
    if (this.isSupported(lang)) {
      this.language.set(lang);
      this.storeLanguage(lang);
      this.transloco.setActiveLang(lang);
      this.updatePrimeNGLocale(lang);
    }
  }

  public getCurrentLanguage(): string {
    return this.language();
  }
}
